import {
  Code,
  Cog,
  ShoppingCart,
  Users,
  Rocket,
  Zap,
  Layers,
  Cpu,
  Smartphone,
  Cloud,
  Workflow,
  GitBranch,
  BarChart3,
  Database,
  Package,
  CreditCard,
  Truck,
  Lightbulb,
  Target as TargetIcon,
  Search,
  Compass,
  MessageSquare,
} from 'lucide-react';

/* ============================================================
   PRODUCT ENGINEERING
   ============================================================ */
export const productEngineering = {
  slug: 'product-engineering',
  eyebrow: 'Product Engineering',
  icon: Code,
  accentClass: 'from-purple-600 to-pink-500',
  lightAccentClass: 'bg-purple-50',
  accentTextClass: 'text-purple-600',

  heroHeadline: 'From MVP to Production-Ready Software, Built for Growth',
  heroSubheadline:
    'Web apps, mobile apps, and custom software engineered for Canadian SMBs, with clean code, honest timelines, and technology choices that fit your budget.',
  primaryCta: { label: 'Book a Product Strategy Call', href: '/contact' },
  secondaryCta: { label: 'See What We Build', href: '#services-grid' },

  aiSummary:
    "Blue Ribbon's Product Engineering builds web apps, mobile apps, and custom SaaS for Canadian SMBs, from MVP to production, with typical MVP delivery in 4 to 6 weeks and full IP ownership by the client.",

  stats: [
    { value: '4–6', suffix: ' wks', label: 'Avg MVP Delivery' },
    { value: 100, suffix: '%', label: 'Client-Owned IP' },
    { value: 40, suffix: '+', label: 'Products Shipped' },
    { value: 99.9, suffix: '%', label: 'Post-Launch Uptime' },
  ],

  problemBar: [
    "Your idea is stuck in a slide deck because agencies quoted 6 months and $250k",
    "Your last dev shop over-engineered a simple app and blew past the timeline",
    "You're worried about being locked in with proprietary code you don't own",
  ],

  whatWeDoHeadline: 'Ship real software fast, without over-engineering',
  whatWeDo: [
    'We build the smallest version of the product that proves value, then evolve it based on what users actually do. No 6-month roadmaps that get outdated in month 2.',
    'Every stack decision is justified in plain English. You own the code, the repo, and the deployment. When we hand off, everything is documented and easy for another team to pick up.',
  ],

  servicesBreakdownTitle: 'What We Build',
  servicesBreakdown: [
    {
      icon: Layers,
      title: 'Web Apps & SaaS',
      description: 'React, Next.js, and Node backends, designed for scale from day one.',
    },
    {
      icon: Smartphone,
      title: 'Mobile Apps',
      description: 'iOS and Android via React Native. One codebase, native performance.',
    },
    {
      icon: Cpu,
      title: 'AI-Enabled Features',
      description: 'ChatGPT-class features embedded properly, without breaking budgets.',
    },
    {
      icon: Cloud,
      title: 'Cloud Migrations',
      description: 'Move from legacy hosting to AWS, Vercel, or Azure with zero downtime.',
    },
    {
      icon: GitBranch,
      title: 'Integrations & APIs',
      description: 'Connect your existing tools with clean APIs that keep working.',
    },
    {
      icon: Rocket,
      title: 'MVP Sprints',
      description: '4–6 week focused sprints to ship a working product to real users.',
    },
  ],

  howItWorks: [
    { title: 'Discovery Sprint', description: 'One week to nail scope, stack, and success metrics.' },
    { title: 'Design + Prototype', description: 'Interactive prototypes before a line of code is written.' },
    { title: 'Build in 2-Week Sprints', description: 'Weekly demos, no black-box builds.' },
    { title: 'Ship + Support', description: 'Deploy, hand over docs, and stay available for iterations.' },
  ],

  whyBRT: [
    { title: 'You Own Everything', description: 'Source code, repo, and deployment credentials transferred on day one.' },
    { title: 'No Over-Engineering', description: 'We pick the simplest tech stack that meets your needs, not the trendiest.' },
    { title: 'Fixed-Scope Sprints', description: 'You know what ships every 2 weeks. No timeline drift, no scope creep.' },
  ],

  proof: {
    stats: [
      { value: 4, suffix: ' wks', label: 'Fastest MVP Delivered' },
      { value: 92, suffix: '%', label: 'Return-Client Rate' },
    ],
    caseStudy: {
      quote: "They shipped a working v1 in 5 weeks. Two years later, 30,000+ users. Same core codebase.",
      client: 'CEO, Vancouver B2B Startup',
      industry: 'SaaS',
    },
  },

  faqs: [
    { question: 'How long does an MVP take to build?', answer: 'Most MVPs ship in 4–6 weeks from kickoff. Complex products with heavy integrations run 8–12 weeks. We commit to a fixed timeline at the end of a 1-week discovery sprint.' },
    { question: 'Do we own the code and IP?', answer: 'Yes, 100%. The source code, GitHub repo, and deployment credentials are transferred to you on day one of build. You own everything.' },
    { question: 'What tech stack do you use?', answer: 'For most projects: React or Next.js frontend, Node.js or Python backend, PostgreSQL or MongoDB database, deployed on Vercel or AWS. We justify every choice in plain English before we start.' },
    { question: 'Can you work with our existing development team?', answer: 'Yes. We can augment your team for a sprint, review architecture, or take on modules independently. We adapt to how your team already works.' },
  ],

  bottomCta: {
    headline: "Have an idea? Let's ship it in weeks, not months.",
    label: 'Book a Product Strategy Call',
    href: '/contact',
  },
};

/* ============================================================
   BUSINESS AUTOMATION
   ============================================================ */
export const businessAutomation = {
  slug: 'business-automation',
  eyebrow: 'Business Automation',
  icon: Cog,
  accentClass: 'from-emerald-600 to-teal-500',
  lightAccentClass: 'bg-emerald-50',
  accentTextClass: 'text-emerald-600',

  heroHeadline: 'Eliminate the Manual Work That Slows Your Business Down',
  heroSubheadline:
    'Custom automation that connects your tools, removes repetitive tasks, and unlocks your internal knowledge, built for the way Canadian SMBs actually operate.',
  primaryCta: { label: 'Book an Automation Review', href: '/contact' },
  secondaryCta: { label: 'See Automation Opportunities', href: '#services-grid' },

  aiSummary:
    "Blue Ribbon's Business Automation identifies repetitive workflows across your business, connects your existing tools with intelligent workflows, and typically delivers a 40% reduction in project timelines with payback under 2 years.",

  stats: [
    { value: 40, suffix: '%', label: 'Timeline Reduction' },
    { value: '<2', suffix: ' yrs', label: 'Typical Payback' },
    { value: 15, suffix: '+ hrs', label: 'Weekly Time Saved' },
    { value: 50, suffix: '+', label: 'Tools We Integrate' },
  ],

  problemBar: [
    'Your team spends hours re-typing the same data across systems that should just talk to each other',
    "Important knowledge lives in one person's head, and when they leave, so does the process",
    "You've bought automation tools but they solve the wrong problem or barely get used",
  ],

  whatWeDoHeadline: 'Automations that pay for themselves, fast',
  whatWeDo: [
    "We start by finding the one workflow with the highest ROI, usually something painfully manual that touches multiple people or systems. Then we automate it, measure the impact, and expand from there.",
    "We also build 'ask-your-business' tools: internal AI assistants that let your team query your own SOPs, contracts, and knowledge in plain English. Suddenly, tribal knowledge becomes searchable.",
  ],

  servicesBreakdownTitle: 'Automation Services',
  servicesBreakdown: [
    { icon: Workflow, title: 'Workflow Automation', description: 'Connect CRM, email, invoicing, and project tools so data flows automatically.' },
    { icon: MessageSquare, title: 'AI-Powered Chatbots', description: 'Customer-facing or internal bots that handle FAQs, bookings, and lookups.' },
    { icon: Database, title: 'Internal Knowledge Access', description: 'Ask your business a question. Get answers from your own SOPs and docs.' },
    { icon: BarChart3, title: 'Reporting Automation', description: 'Nightly reports that used to take a person 2 hours, now run automatically.' },
    { icon: Package, title: 'Document Automation', description: 'Contract, invoice, and proposal generation from templates and data.' },
    { icon: Zap, title: 'Trigger-Based Workflows', description: 'When X happens, do Y and Z, reliably, forever, without human intervention.' },
  ],

  howItWorks: [
    { title: 'Automation Audit', description: 'Map every manual workflow. Rank by time saved × frequency = ROI.' },
    { title: 'Pilot One Win', description: 'Start with the highest-ROI automation. Prove value in 2–4 weeks.' },
    { title: 'Expand', description: 'Roll out to adjacent workflows once the team trusts the approach.' },
    { title: 'Monitor & Refine', description: 'Continuously improve as your business evolves.' },
  ],

  whyBRT: [
    { title: 'Start Small, Prove Fast', description: 'One high-value workflow first. No big-bang, all-or-nothing programs.' },
    { title: 'Tool-Agnostic', description: "We work with whatever you already use, n8n, Zapier, Make, custom code, or whatever fits best." },
    { title: 'Documented, Not Locked-In', description: 'Every automation is documented so your team can maintain it, or hand it to any other partner.' },
  ],

  proof: {
    stats: [
      { value: 15, suffix: '+ hrs', label: 'Avg Weekly Time Saved' },
      { value: 87, suffix: '%', label: 'Error Reduction' },
    ],
    caseStudy: {
      quote: 'Blue Ribbon rebuilt our quoting workflow. What used to take 4 hours now takes 12 minutes. And it never gets a decimal wrong.',
      client: 'Owner, BC Manufacturing SMB',
      industry: 'Manufacturing',
    },
  },

  faqs: [
    { question: 'How do we know which workflow to automate first?', answer: 'We run a 1-week automation audit, mapping every manual process, then ranking by time-saved × frequency = ROI. You get a prioritized list. We recommend starting with the highest-ROI single workflow.' },
    { question: 'Will we still have control over our automations?', answer: 'Absolutely. Every automation is documented, and we build with tools your team can maintain (n8n, Zapier, Make, or custom code we hand over). You are never locked in.' },
    { question: 'What tools do you integrate with?', answer: 'Salesforce, HubSpot, QuickBooks, Xero, Slack, Google Workspace, Microsoft 365, Shopify, Stripe, SharePoint, and 50+ others. If it has an API, we can integrate it.' },
    { question: 'How long until an automation shows ROI?', answer: 'Most single-workflow automations show ROI within 6–12 weeks. Full business-wide payback is typically under 2 years, often much less.' },
  ],

  bottomCta: {
    headline: 'Start with one high-value workflow. Prove the value. Then scale.',
    label: 'Book an Automation Review',
    href: '/contact',
  },
};

/* ============================================================
   ERP & ECOMMERCE
   ============================================================ */
export const erpEcommerce = {
  slug: 'erp-ecommerce',
  eyebrow: 'ERP & Ecommerce',
  icon: ShoppingCart,
  accentClass: 'from-orange-600 to-amber-500',
  lightAccentClass: 'bg-orange-50',
  accentTextClass: 'text-orange-600',

  heroHeadline: 'Unified Commerce: One System for Sales, Inventory, and Operations',
  heroSubheadline:
    'Modern ERP and ecommerce implementations that scale with your business, from first Shopify store to multi-location, multi-channel operations.',
  primaryCta: { label: 'Book an ERP / Ecommerce Consult', href: '/contact' },
  secondaryCta: { label: 'See Platforms We Work With', href: '#services-grid' },

  aiSummary:
    "Blue Ribbon implements ERP systems (Odoo, Zoho, ERPNext) and ecommerce platforms (Shopify, WooCommerce) for Canadian SMBs, delivering unified sales, inventory, and finance operations with 3× faster order-to-cash cycles on average.",

  stats: [
    { value: '3×', suffix: '', label: 'Faster Order Flow' },
    { value: 5, suffix: '+', label: 'Platforms Supported' },
    { value: 30, suffix: '+', label: 'Implementations Delivered' },
    { value: 60, suffix: '%', label: 'Avg Manual-Work Reduction' },
  ],

  problemBar: [
    'Your team uses one tool for sales, another for accounting, and a spreadsheet for inventory, with data that never lines up',
    "You've outgrown your starter Shopify setup but enterprise ERPs quote $250k+ implementations",
    "Every month-end feels like a scavenger hunt because financials don't match operations",
  ],

  whatWeDoHeadline: 'One source of truth for your entire business',
  whatWeDo: [
    "We implement modern, SMB-friendly ERPs like Odoo, Zoho, and ERPNext that give you enterprise-grade capabilities without enterprise-grade prices or timelines.",
    "For ecommerce, we set up or migrate Shopify, WooCommerce, and headless commerce stacks, then connect them cleanly to your ERP so inventory, orders, and financials stay in sync across channels.",
  ],

  servicesBreakdownTitle: 'ERP & Ecommerce Services',
  servicesBreakdown: [
    { icon: Database, title: 'ERP Implementation', description: 'Odoo, Zoho, ERPNext, configured to your business, not the other way around.' },
    { icon: ShoppingCart, title: 'Ecommerce Setup', description: 'Shopify, WooCommerce, and headless stacks tuned for Canadian shipping and tax.' },
    { icon: Package, title: 'Inventory Sync', description: 'One inventory across web, retail, wholesale, and marketplaces.' },
    { icon: CreditCard, title: 'Order-to-Cash Automation', description: 'Order → invoice → payment → fulfillment, hands-free.' },
    { icon: Truck, title: 'Shipping & 3PL Integration', description: 'Connect Canada Post, ShipStation, and 3PLs directly to your stack.' },
    { icon: BarChart3, title: 'Unified Reporting', description: 'Real-time dashboards showing sales, margin, inventory, and cash together.' },
  ],

  platformBadges: ['Odoo', 'Zoho', 'ERPNext', 'Shopify', 'WooCommerce', 'BigCommerce'],

  howItWorks: [
    { title: 'Business Mapping', description: 'Document your real workflows, not the vendor demo.' },
    { title: 'Platform Selection', description: 'Pick the ERP or commerce platform that fits your business and budget.' },
    { title: 'Phased Rollout', description: 'Go-live in modules to keep operations running smoothly.' },
    { title: 'Training & Support', description: 'Your team owns it. We stay available for questions and iterations.' },
  ],

  whyBRT: [
    { title: 'Platform-Agnostic', description: 'We recommend what fits your business, not what earns us commission.' },
    { title: 'Faster Rollouts', description: 'Typical ERP go-live in 8–12 weeks, not 6–12 months.' },
    { title: 'Real Business Focus', description: 'We implement what your team will actually use, not every module the software offers.' },
  ],

  proof: {
    stats: [
      { value: 60, suffix: '%', label: 'Faster Order-to-Cash' },
      { value: 30, suffix: '%', label: 'Inventory Cost Reduction' },
    ],
    caseStudy: {
      quote: 'Odoo replaced 4 different tools and 2 spreadsheets. Month-end went from 5 days to half a day.',
      client: 'Ops Director, BC Distribution Company',
      industry: 'Distribution',
    },
  },

  faqs: [
    { question: 'Which ERP is right for my business?', answer: "It depends on size, industry, and existing stack. For most SMBs under 50 employees, Odoo or Zoho are the sweet spot. For manufacturing or distribution, ERPNext or Odoo. We recommend based on your actual workflows after a 1-week mapping session." },
    { question: 'How long does an ERP implementation take?', answer: 'Typical SMB ERP go-live is 8–12 weeks in phased modules (finance first, then inventory, then sales). Complex multi-entity setups run 12–20 weeks.' },
    { question: 'Can you connect our ecommerce store to our ERP?', answer: 'Yes, this is one of our most common projects. Shopify + Odoo, WooCommerce + Zoho, custom + ERPNext, etc. Inventory, orders, and financials stay in sync automatically.' },
    { question: 'What about Canadian shipping, tax, and compliance?', answer: 'Fully handled. GST/PST/HST, Canada Post integrations, provincial variations, and PIPEDA-compliant data handling are baked into every implementation.' },
  ],

  bottomCta: {
    headline: 'One system. One source of truth. Real-time visibility across your business.',
    label: 'Book an ERP / Ecommerce Consult',
    href: '/contact',
  },
};

/* ============================================================
   CONSULTING & ADVISORY
   ============================================================ */
export const consulting = {
  slug: 'consulting',
  eyebrow: 'Consulting & Advisory',
  icon: Users,
  accentClass: 'from-indigo-600 to-violet-500',
  lightAccentClass: 'bg-indigo-50',
  accentTextClass: 'text-indigo-600',

  heroHeadline: 'Honest, Practical Advice, Not a Sales Pitch',
  heroSubheadline:
    'Vendor-agnostic strategy, project assessments, and AI readiness reviews for Canadian owner-led businesses. We tell you what works, what doesn\'t, and what to fix first.',
  primaryCta: { label: 'Book a Discovery Call', href: '/contact' },
  secondaryCta: { label: 'See Consulting Services', href: '#services-grid' },

  aiSummary:
    "Blue Ribbon's Consulting & Advisory delivers vendor-agnostic strategy for Canadian SMBs: AI readiness assessments, project audits, tech-stack reviews, and digital roadmaps, typically in 4-8 week engagements with clear go/no-go recommendations.",

  stats: [
    { value: '4–8', suffix: ' wks', label: 'Typical Engagement Length' },
    { value: '$0', suffix: '', label: 'Vendor Kickbacks' },
    { value: 100, suffix: '%', label: 'BC Legal Jurisdiction' },
    { value: 20, suffix: '+ yrs', label: 'Combined Leadership' },
  ],

  problemBar: [
    "You've been quoted three wildly different solutions for the same problem and don't know who to trust",
    "You're worried a project already in flight is going sideways, but no one internally will say so",
    "You want to explore AI or automation but don't know where to start, or who's genuinely unbiased",
  ],

  whatWeDoHeadline: 'Advice you can actually act on',
  whatWeDo: [
    "We don't sell software licenses and we don't take vendor kickbacks. That means our advice is anchored in what's right for your business, not what earns us commission.",
    "Every engagement ends with a clear, prioritized action plan, in plain English, that you can execute yourself, with your existing team, or with a partner of your choice.",
  ],

  servicesBreakdownTitle: 'Consulting Services',
  servicesBreakdown: [
    { icon: Lightbulb, title: 'AI Readiness Assessment', description: 'Where AI actually fits in your business, and where it does not.' },
    { icon: Search, title: 'Project Health Audit', description: 'An unbiased second opinion on projects that feel off-track.' },
    { icon: Compass, title: 'Digital Roadmap', description: '12-18 month plan for tech, automation, and AI investments.' },
    { icon: TargetIcon, title: 'Tech-Stack Review', description: 'What to keep, replace, or retire. And what to buy vs. build.' },
    { icon: Users, title: 'Workforce & Ops Strategy', description: 'Where technology reduces headcount pressure, and where it should not.' },
    { icon: BarChart3, title: 'Vendor Selection', description: 'Neutral RFP support and vendor evaluation. We help you pick, not push a product.' },
  ],

  howItWorks: [
    { title: 'Kickoff & Discovery', description: 'Stakeholder interviews, current-state mapping, goal alignment.' },
    { title: 'Deep Analysis', description: 'We test assumptions, review data, and stress-test proposed solutions.' },
    { title: 'Recommendations', description: 'Prioritized action plan, go/no-go calls, and vendor shortlists.' },
    { title: 'Handoff', description: 'You execute (with your team or partners). We stay available for follow-ups.' },
  ],

  whyBRT: [
    { title: 'Vendor-Agnostic', description: "We do not resell software. No commission. No favoritism. What we recommend is what we would do ourselves." },
    { title: 'Written Recommendations', description: 'Everything documented in plain-English reports your team can action.' },
    { title: 'BC-Based, Bilingual-Capable', description: 'Governed by BC and Canadian law. Bilingual delivery for Quebec-based businesses.' },
  ],

  proof: {
    stats: [
      { value: 92, suffix: '%', label: 'Recommendations Adopted' },
      { value: '4–8', suffix: ' wks', label: 'Typical Engagement Length' },
    ],
    caseStudy: {
      quote: 'They saved us from a $180k software mistake. That single recommendation paid for the whole engagement 10× over.',
      client: 'CEO, Vancouver Professional Services Firm',
      industry: 'Professional Services',
    },
  },

  faqs: [
    { question: 'How is your consulting different from a big-firm consultancy?', answer: 'Three ways: (1) we do not resell software or take vendor commissions, (2) engagements are 4-8 weeks, not 6 months, and (3) senior consultants do the actual work, not junior associates.' },
    { question: 'What does an AI readiness assessment include?', answer: 'A 3-week engagement that maps every workflow, ranks AI opportunities by ROI, tests 2-3 top opportunities against real data, and delivers a prioritized action plan, including go/no-go recommendations on any AI tools you are considering.' },
    { question: 'Can you audit a project that is already in flight?', answer: 'Yes, this is one of our most common engagements. We do independent, confidential health audits with a clear traffic-light report on scope, timeline, budget, and technical risk. Typically 2–3 weeks.' },
    { question: 'Do you help with vendor selection?', answer: 'Yes. We support neutral RFPs, vendor evaluations, and contract review. We do not take referral fees from any vendor, so our recommendations reflect what actually fits your business.' },
  ],

  bottomCta: {
    headline: 'Get an honest second opinion. See what to fix first.',
    label: 'Book a Discovery Call',
    href: '/contact',
  },
};

export const legacyServicesMap = {
  'product-engineering': productEngineering,
  'business-automation': businessAutomation,
  'erp-ecommerce': erpEcommerce,
  consulting,
};
